import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  FormControl,
  Row,
  Col,
  Pagination,
  Breadcrumb,
  Card,
} from "react-bootstrap";
import {
  FaHeart,
  FaMapMarkerAlt,
  FaRegHeart,
  FaBed,
  FaBath,
  FaRulerCombined,
  FaChevronDown,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import constusbanner from "../../Assests/back-header-1.png";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PropertyList = () => {
  const [allProperties, setAllProperties] = useState([]); // Store all fetched properties
  const [filteredProperties, setFilteredProperties] = useState([]); // Store filtered properties
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCity, setSearchCity] = useState("");
  const [submittedCity, setSubmittedCity] = useState("");
  const navigate = useNavigate();
  const query = useQuery();
  const city = query.get("city");

  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [beds, setBeds] = useState("");
  const [bathroom, setBathroom] = useState("");
  const [favorites, setFavorites] = useState([]);

  // Filter dropdowns state
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isBedsOpen, setIsBedsOpen] = useState(false);
  const [isHomeTypeOpen, setIsHomeTypeOpen] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [bedOption, setBedOption] = useState("");
  const [homeType, setHomeType] = useState([]);
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");

  const minPriceParam = query.get("minPrice");
  const maxPriceParam = query.get("maxPrice");

  // more filter dropdowns state
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [bathOption, setBathOption] = useState("");
  const [baths, setBaths] = useState("");
  const [squareFeetMin, setSquareFeetMin] = useState("");
  const [squareFeetMax, setSquareFeetMax] = useState("");
  const [hasPhotos, setHasPhotos] = useState(false);
  const [keywords, setKeywords] = useState("");

  const bathOptions = ["Any", "1+", "2+", "3+", "4+"];

  const handleBathsSelect = (option) => {
    setBathOption(option);
    if (option === "Any") {
      setBaths("");
    } else {
      setBaths(option);
    }
  };

  const applySquareFeetRange = () => {
    // The values will already be in state, so we just need to close the dropdown
    setIsMoreOpen(false);
  };

  const getMoreButtonText = () => {
    let activeFilters = [];

    if (baths) activeFilters.push(`${baths} Baths`);
    if (squareFeetMin || squareFeetMax) activeFilters.push("Sq Ft");
    if (hasPhotos) activeFilters.push("Has Photos");
    if (keywords) activeFilters.push("Keywords");

    if (activeFilters.length === 0) return "More";
    return `More (${activeFilters.length})`;
  };

  const [sortOption, setSortOption] = useState("newest");
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: "newest", label: "Most Recent" },
    { value: "oldest", label: "Oldest" },
    { value: "expensive", label: "Most Expensive" },
    { value: "affordable", label: "Least Expensive" },
    { value: "bedrooms", label: "Most Bedrooms" },
  ];

  const handleSortSelect = (value) => {
    setSortOption(value);
    setIsSortOpen(false);
    sortProperties(value);
  };

  const getCurrentSortLabel = () => {
    const option = sortOptions.find((opt) => opt.value === sortOption);
    return option ? option.label : "Sort";
  };

  const sortProperties = (option) => {
    if (!filteredProperties.length) return;

    let sorted = [...filteredProperties];

    switch (option) {
      case "newest":
        // Sort by most recent (assuming you have a ListingDate field)
        sorted.sort((a, b) => {
          const dateA = a.ListingDate ? new Date(a.ListingDate) : new Date(0);
          const dateB = b.ListingDate ? new Date(b.ListingDate) : new Date(0);
          return dateB - dateA; // Newest first
        });
        break;

      case "oldest":
        // Sort by oldest
        sorted.sort((a, b) => {
          const dateA = a.ListingDate ? new Date(a.ListingDate) : new Date(0);
          const dateB = b.ListingDate ? new Date(b.ListingDate) : new Date(0);
          return dateA - dateB; // Oldest first
        });
        break;

      case "expensive":
        // Sort by price, most expensive first
        sorted.sort((a, b) => Number(b.ListPrice) - Number(a.ListPrice));
        break;

      case "affordable":
        // Sort by price, least expensive first
        sorted.sort((a, b) => Number(a.ListPrice) - Number(b.ListPrice));
        break;

      case "bedrooms":
        // Sort by number of bedrooms
        sorted.sort((a, b) => {
          const bedsA = a.BedroomsTotal ? Number(a.BedroomsTotal) : 0;
          const bedsB = b.BedroomsTotal ? Number(b.BedroomsTotal) : 0;
          return bedsB - bedsA; // Most bedrooms first
        });
        break;

      default:
        // Default to newest
        sorted.sort((a, b) => {
          const dateA = a.ListingDate ? new Date(a.ListingDate) : new Date(0);
          const dateB = b.ListingDate ? new Date(b.ListingDate) : new Date(0);
          return dateB - dateA;
        });
    }

    setFilteredProperties(sorted);
  };

  useEffect(() => {
    if (filteredProperties.length > 0) {
      sortProperties(sortOption);
    }
  }, [filteredProperties.length]);

  // ----------------------------------------------

  // Price ranges for the dropdown
  const priceRanges = [
    { min: "", max: "", label: "Any Price" },
    { min: "0", max: "100000", label: "$0-$100k" },
    { min: "100000", max: "200000", label: "$100k-$200k" },
    { min: "200000", max: "300000", label: "$200k-$300k" },
    { min: "300000", max: "500000", label: "$300k-$500k" },
    { min: "500000", max: "750000", label: "$500k-$750k" },
    { min: "750000", max: "1000000", label: "$750k-$1M" },
    { min: "1000000", max: "", label: "$1M+" },
  ];

  // Bed options
  const bedOptions = ["Any", "0+", "1+", "2+", "3+", "4+", "5+"];

  // Home type options
  const homeTypeOptions = ["House", "Condo", "Townhouse"];

  // Function to capitalize the first letter of each word in a string
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    if (minPriceParam) {
      setMinPrice(minPriceParam);
      setCustomMin(minPriceParam);
      setPriceRange((prev) => ({ ...prev, min: minPriceParam }));
    }
    if (maxPriceParam) {
      setMaxPrice(maxPriceParam);
      setCustomMax(maxPriceParam);
      setPriceRange((prev) => ({ ...prev, max: maxPriceParam }));
    }
  }, [minPriceParam, maxPriceParam]);

  useEffect(() => {
    if (city) {
      setSearchCity(capitalizeFirstLetter(city));
      setSubmittedCity(capitalizeFirstLetter(city));
    }
  }, [city]);

  useEffect(() => {
    let apiUrl = "https://handarealtor.ca/property-list";

    if (city) {
      apiUrl += `?city=${encodeURIComponent(city)}`;
    }

    setLoading(true);

    axios
      .get(apiUrl)
      .then((res) => {
        if (res.data.value) {
          let propertyList = res.data.value;

          propertyList = propertyList.filter(
            (p) =>
              p.TransactionType !== "Lease" &&
              p.TransactionType !== "For Lease",
          );

          // Store all properties
          setAllProperties(propertyList);

          // Initial filtering of properties
          applyFilters(propertyList);

          fetchImages(propertyList);
        }
      })
      .catch((err) => {
        console.error("Error fetching property list:", err);
        setError("Failed to load properties.");
      })
      .finally(() => setLoading(false));
  }, [city]);

  // Apply all filters and update the filtered properties list
  const applyFilters = (properties) => {
    let filtered = [...properties];

    // Existing filters
    // Filter by price range
    if (priceRange.min) {
      filtered = filtered.filter(
        (p) => Number(p.ListPrice) >= Number(priceRange.min),
      );
    }
    if (priceRange.max) {
      filtered = filtered.filter(
        (p) => Number(p.ListPrice) <= Number(priceRange.max),
      );
    }

    // Filter by bedrooms
    if (beds && beds !== "Any") {
      const minBeds = parseInt(beds.replace("+", ""), 10);
      filtered = filtered.filter(
        (p) => p.BedroomsTotal && Number(p.BedroomsTotal) >= minBeds,
      );
    }

    // Filter by home type
    if (homeType.length > 0) {
      filtered = filtered.filter((p) => {
        const propertyType = p.PropertyType || p.PropertySubType || "";
        return homeType.some((type) =>
          propertyType.toLowerCase().includes(type.toLowerCase()),
        );
      });
    }

    // New filters
    // Filter by bathrooms
    if (baths && baths !== "Any") {
      const minBaths = parseInt(baths.replace("+", ""), 10);
      filtered = filtered.filter(
        (p) =>
          p.BathroomsTotalInteger &&
          Number(p.BathroomsTotalInteger) >= minBaths,
      );
    }

    // Filter by square feet
    if (squareFeetMin) {
      filtered = filtered.filter(
        (p) => p.LivingArea && Number(p.LivingArea) >= Number(squareFeetMin),
      );
    }
    if (squareFeetMax) {
      filtered = filtered.filter(
        (p) => p.LivingArea && Number(p.LivingArea) <= Number(squareFeetMax),
      );
    }

    // Filter by has photos
    if (hasPhotos) {
      filtered = filtered.filter(
        (p) => images[p.ListingKey] && images[p.ListingKey].length > 0,
      );
    }

    // Filter by keywords
    if (keywords.trim()) {
      const searchTerms = keywords.toLowerCase().split(" ");
      filtered = filtered.filter((p) => {
        const propertyText = `${p.PublicRemarks || ""} ${p.StreetName || ""} ${
          p.StreetNumber || ""
        } ${p.City || ""} ${p.PostalCode || ""} ${p.PropertyType || ""} ${
          p.PropertySubType || ""
        }`.toLowerCase();

        return searchTerms.every((term) => propertyText.includes(term));
      });
    }

    // Remove invalid prices (less than 100)
    filtered = filtered.filter(
      (p) => Number(p.ListPrice) < 1 || Number(p.ListPrice) > 100,
    );

    setFilteredProperties(filtered);
    setTotalPages(Math.ceil(filtered.length / propertiesPerPage));

    // Reset to first page when filters change
    setCurrentPage(1);
  };

  // Re-apply filters whenever filter criteria change
  useEffect(() => {
    if (allProperties.length > 0) {
      applyFilters(allProperties);
    }
  }, [
    priceRange,
    beds,
    homeType,
    baths,
    squareFeetMin,
    squareFeetMax,
    hasPhotos,
    keywords,
    propertiesPerPage,
    images,
  ]);

  const fetchImages = async (propertyList) => {
    const imageMap = {};

    await Promise.all(
      propertyList.map(async (property) => {
        try {
          const listingKey = property.ListingKey;
          const response = await axios.get(
            `https://handarealtor.ca/property-images/${listingKey}`,
          );

          if (response.data.success && response.data.images.length > 0) {
            imageMap[listingKey] = response.data.images;
          }
        } catch (err) {
          console.error(`Image error for ${property.ListingKey}:`, err.message);
        }
      }),
    );

    setImages((prev) => ({ ...prev, ...imageMap }));
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const queryParams = new URLSearchParams();

    // Capitalize the city name before setting it in the URL
    if (searchCity.trim()) {
      const capitalizedCity = capitalizeFirstLetter(searchCity.trim());
      queryParams.set("city", capitalizedCity);
      setSubmittedCity(capitalizedCity); // Update submittedCity when search is submitted
    } else {
      setSubmittedCity(""); // Clear submitted city if search is empty
    }

    if (priceRange.min) queryParams.set("minPrice", priceRange.min);
    if (priceRange.max) queryParams.set("maxPrice", priceRange.max);

    navigate(`?${queryParams.toString()}`);
    setCurrentPage(1);
  };

  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(
    indexOfFirstProperty,
    indexOfLastProperty,
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const renderPaginationItems = () => {
    let items = [];

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />,
    );

    items.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>,
    );

    if (currentPage > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (
      let number = Math.max(2, currentPage - 1);
      number <= Math.min(totalPages - 1, currentPage + 1);
      number++
    ) {
      if (number > 1 && number < totalPages) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </Pagination.Item>,
        );
      }
    }

    if (currentPage < totalPages - 2 && totalPages > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }

    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />,
    );

    return items;
  };

  const toggleFavorite = (listingKey) => {
    setFavorites((prevFavorites) =>
      prevFavorites.includes(listingKey)
        ? prevFavorites.filter((key) => key !== listingKey)
        : [...prevFavorites, listingKey],
    );
  };

  // Filter handling functions
  const handlePriceSelect = (min, max, label) => {
    setPriceRange({ min, max });
    setMinPrice(min);
    setMaxPrice(max);
    setIsPriceOpen(false);
  };

  const applyCustomPrice = () => {
    setPriceRange({ min: customMin, max: customMax });
    setMinPrice(customMin);
    setMaxPrice(customMax);
    setIsPriceOpen(false);
  };

  const handleBedsSelect = (option) => {
    setBedOption(option);
    if (option === "Any") {
      setBeds("");
    } else {
      setBeds(option);
    }
    setIsBedsOpen(false);
  };

  const handleHomeTypeSelect = (type) => {
    if (homeType.includes(type)) {
      setHomeType(homeType.filter((item) => item !== type));
    } else {
      setHomeType([...homeType, type]);
    }
  };

  const handleOutsideClick = () => {
    setIsPriceOpen(false);
    setIsBedsOpen(false);
    setIsHomeTypeOpen(false);
    setIsMoreOpen(false);
    setIsSortOpen(false);
  };

  // Format the display of the price button
  const getPriceButtonText = () => {
    if (!priceRange.min && !priceRange.max) return "Any Price";
    if (priceRange.min && !priceRange.max)
      return `$${formatPrice(priceRange.min)}+`;
    if (!priceRange.min && priceRange.max)
      return `Up to $${formatPrice(priceRange.max)}`;
    return `$${formatPrice(priceRange.min)} - $${formatPrice(priceRange.max)}`;
  };

  // Format price with commas
  const formatPrice = (price) => {
    return Number(price).toLocaleString();
  };

  // Get the text for home type button
  const getHomeTypeButtonText = () => {
    if (homeType.length === 0) return "Home Type";
    if (homeType.length === homeTypeOptions.length) return "All Home Types";
    return homeType.join(", ");
  };

  return (
    <>
      <section
        className="contact-banner d-flex align-items-center justify-content-center text-center text-white"
        style={{
          backgroundImage: `url(${constusbanner})`,
        }}
      >
        <div className="overlay"></div>

        <div className="content w-100 container pt-5">
          <h1 className="text-start banner-title">Properties Listing</h1>
        </div>
      </section>
      <Container className="py-5">
        <Form onSubmit={handleSearch} className="mb-4">
          <div className="d-flex align-items-center border rounded-pill bg-white shadow-sm overflow-hidden searchbar__main">
            {/* Search Input */}
            <FormControl
              type="text"
              placeholder="City, Neighbourhood, Address or MLS® number"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              className="border-0 px-3 py-2 flex-grow-1 shadow-none"
              style={{
                borderRadius: "50px 0 0 50px",
                outline: "none",
                boxShadow: "none",
              }}
              onFocus={(e) => (e.target.style.boxShadow = "none")}
              onBlur={(e) =>
                setSearchCity(capitalizeFirstLetter(e.target.value))
              }
            />

            {/* Search Button */}
            <Button
              type="submit"
              className="rounded-circle border-0 d-flex align-items-center justify-content-center ms-2 shadow-none search__button"
              onFocus={(e) => (e.target.style.boxShadow = "none")}
            >
              <i className="bi bi-search text-white"></i>
            </Button>
          </div>
        </Form>

        {/* Property Filter Bar */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          {/* For Sale Button */}
          <button className="d-flex align-items-center justify-content-center for__sale__button">
            For Sale
          </button>

          {/* Price Filter */}
          <div className="position-relative">
            <button
              className="d-flex align-items-center justify-content-between for__sale__button"
              onClick={() => {
                setIsPriceOpen(!isPriceOpen);
                setIsBedsOpen(false);
                setIsHomeTypeOpen(false);
              }}
            >
              <span>{getPriceButtonText()}</span>
              <FaChevronDown className="ms-2" size={14} />
            </button>

            {isPriceOpen && (
              <div
                className="position-absolute mt-2 bg-white border border-gray-300 rounded shadow-lg z-3"
                style={{ width: "300px" }}
              >
                <div className="p-3">
                  <h4 className="fw-semibold mb-2">Price Range</h4>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {priceRanges.map((range, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`px-3 py-2 text-sm border ${
                          priceRange.min === range.min &&
                          priceRange.max === range.max
                            ? "bg-light text-dark"
                            : "bg-light text-dark"
                        } rounded`}
                        style={{ flex: "0 0 calc(50% - 0.5rem)" }}
                        onClick={() =>
                          handlePriceSelect(range.min, range.max, range.label)
                        }
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="Min $"
                      className="form-control"
                      value={customMin}
                      onChange={(e) => setCustomMin(e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="text"
                      placeholder="Max $"
                      className="form-control"
                      value={customMax}
                      onChange={(e) => setCustomMax(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="w-100 px-3 py-2 for__sale__button"
                    onClick={applyCustomPrice}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Beds Filter */}
          <div className="position-relative">
            <button
              className="d-flex align-items-center justify-content-between for__sale__button"
              onClick={() => {
                setIsBedsOpen(!isBedsOpen);
                setIsPriceOpen(false);
                setIsHomeTypeOpen(false);
              }}
            >
              <span>{bedOption || "0+ Bed"}</span>
              <FaChevronDown className="ms-2" size={14} />
            </button>

            {isBedsOpen && (
              <div
                className="position-absolute mt-2 bg-white border border-gray-300 rounded shadow-lg z-3"
                style={{ width: "200px" }}
              >
                <div className="p-3">
                  <h4 className="fw-semibold mb-2">Bedrooms</h4>
                  <div className="d-flex flex-column gap-2">
                    {bedOptions.map((option, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`px-3 py-2 text-start border ${
                          bedOption === option
                            ? "bg-light text-dark"
                            : "bg-light text-dark"
                        } rounded`}
                        onClick={() => handleBedsSelect(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Home Type Filter */}
          <div className="position-relative">
            <button
              className="d-flex align-items-center justify-content-between for__sale__button"
              onClick={() => {
                setIsHomeTypeOpen(!isHomeTypeOpen);
                setIsPriceOpen(false);
                setIsBedsOpen(false);
              }}
            >
              <span>{getHomeTypeButtonText()}</span>
              <FaChevronDown className="ms-2" size={14} />
            </button>

            {isHomeTypeOpen && (
              <div
                className="position-absolute mt-2 bg-white border border-gray-300 rounded shadow-lg z-3"
                style={{ width: "200px" }}
              >
                <div className="p-3">
                  <h4 className="fw-semibold mb-2">Home Type</h4>
                  <div className="d-flex flex-column gap-2">
                    {homeTypeOptions.map((type, idx) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center gap-2 px-3 py-2 hover-bg-light rounded cursor-pointer"
                        onClick={() => handleHomeTypeSelect(type)}
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={homeType.includes(type)}
                          onChange={() => {}}
                          className="form-check-input"
                        />
                        <span>{type}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="w-100 mt-3 px-3 py-2 for__sale__button"
                    onClick={() => setIsHomeTypeOpen(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* More Type Filter */}
          <div className="position-relative">
            <button
              className="d-flex align-items-center justify-content-between for__sale__button"
              onClick={() => {
                setIsMoreOpen(!isMoreOpen);
                setIsPriceOpen(false);
                setIsBedsOpen(false);
                setIsHomeTypeOpen(false);
              }}
            >
              <span>{getMoreButtonText()}</span>
              <FaChevronDown className="ms-2" size={14} />
            </button>

            {isMoreOpen && (
              <div
                className="position-absolute mt-2 bg-white border border-gray-300 rounded shadow-lg z-3"
                style={{ width: "300px" }}
              >
                <div className="p-3">
                  {/* Bathrooms Section */}
                  <h4 className="fw-semibold mb-2">Bathrooms</h4>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {bathOptions.map((option, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`px-3 py-2 text-sm border ${
                          bathOption === option
                            ? "bg-light text-dark"
                            : "bg-light text-dark"
                        } rounded`}
                        style={{ flex: "0 0 calc(33% - 0.5rem)" }}
                        onClick={() => handleBathsSelect(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  {/* Square Feet Section */}
                  <h4 className="fw-semibold mb-2 mt-3">Square Feet</h4>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="Min"
                      className="form-control"
                      value={squareFeetMin}
                      onChange={(e) => setSquareFeetMin(e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="text"
                      placeholder="Max"
                      className="form-control"
                      value={squareFeetMax}
                      onChange={(e) => setSquareFeetMax(e.target.value)}
                    />
                  </div>

                  {/* Has Photos Section */}
                  <div className="mb-3">
                    <div
                      className="d-flex align-items-center gap-2  hover-bg-light rounded cursor-pointer"
                      onClick={() => setHasPhotos(!hasPhotos)}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={hasPhotos}
                        onChange={() => {}}
                        className="form-check-input"
                      />
                      <span>Has Photos</span>
                    </div>
                  </div>

                  {/* Keywords Section */}
                  <h4 className="fw-semibold mb-2">Keywords</h4>
                  <input
                    type="text"
                    placeholder="Eg: Pool, Garage etc.."
                    className="form-control mb-3"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                  />

                  <button
                    type="button"
                    className="w-100 px-3 py-2 for__sale__button"
                    onClick={() => setIsMoreOpen(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* sort type */}
          <div className="position-relative ms-auto">
            <button
              className="d-flex align-items-center justify-content-between for__sale__button"
              onClick={() => {
                setIsSortOpen(!isSortOpen);
                setIsPriceOpen(false);
                setIsBedsOpen(false);
                setIsHomeTypeOpen(false);
                setIsMoreOpen(false);
              }}
            >
              <span>Sort: {getCurrentSortLabel()}</span>
              <FaChevronDown className="ms-2" size={14} />
            </button>

            {isSortOpen && (
              <div
                className="position-absolute end-0 mt-2 bg-white border border-gray-300 rounded shadow-lg z-3"
                style={{ width: "200px" }}
              >
                <div className="p-3">
                  <h4 className="fw-semibold mb-2">Sort By</h4>
                  <div className="d-flex flex-column gap-2">
                    <div className="fw-semibold mb-1">Days on Site:</div>
                    <button
                      type="button"
                      className={`px-3 py-2 text-start border ${
                        sortOption === "newest"
                          ? "bg-light text-dark"
                          : "bg-light text-dark"
                      } rounded`}
                      onClick={() => handleSortSelect("newest")}
                    >
                      Most Recent
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-2 text-start border ${
                        sortOption === "oldest"
                          ? "bg-light text-dark"
                          : "bg-light text-dark"
                      } rounded`}
                      onClick={() => handleSortSelect("oldest")}
                    >
                      Oldest
                    </button>

                    <div className="fw-semibold mb-1 mt-2">Price:</div>
                    <button
                      type="button"
                      className={`px-3 py-2 text-start border ${
                        sortOption === "expensive"
                          ? "bg-light text-dark"
                          : "bg-light text-dark"
                      } rounded`}
                      onClick={() => handleSortSelect("expensive")}
                    >
                      Most Expensive
                    </button>
                    {/* <button
                    type="button"
                    className={`px-3 py-2 text-start border ${
                      sortOption === "affordable"
                        ? "bg-light text-dark"
                        : "bg-light text-dark"
                    } rounded`}
                    onClick={() => handleSortSelect("affordable")}
                  >
                    Least Expensive
                  </button> */}

                    <div className="fw-semibold mb-1 mt-2">Bedrooms:</div>
                    <button
                      type="button"
                      className={`px-3 py-2 text-start border ${
                        sortOption === "bedrooms"
                          ? "bg-light text-dark"
                          : "bg-light text-dark"
                      } rounded`}
                      onClick={() => handleSortSelect("bedrooms")}
                    >
                      Most Bedrooms
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Invisible overlay to detect clicks outside dropdowns */}
          {(isPriceOpen ||
            isBedsOpen ||
            isHomeTypeOpen ||
            isMoreOpen ||
            isSortOpen) && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 z-2"
              onClick={handleOutsideClick}
              style={{ background: "transparent" }}
            />
          )}
        </div>

        <Row className="mb-3">
          <Col xs={12}>
            <Breadcrumb>
              <Breadcrumb.Item href="/">
                <span className="text-dark">Home</span>
              </Breadcrumb.Item>
              <Breadcrumb.Item active>
                {submittedCity ? submittedCity : "All Properties"}
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          <Col xs={12}>
            {searchCity && (
              <h2 className="fw-semibold mb-3 fs-3">
                {submittedCity} Real Estate Listings
              </h2>
            )}
          </Col>

          <Col
            xs={12}
            className="d-flex justify-content-between flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-0"
          >
            <div className="d-flex flex-column flex-md-row gap-2">
              <button className="custom-tab-btn for__sale__buttons">
                Market Stats
              </button>
              <button className="custom-tab-btn for__sale__buttons">
                Neighbourhoods
              </button>
              <button className="custom-tab-btn for__sale__buttons">
                Recently Sold
              </button>
            </div>

            <p className="fw-semibold mb-2 mb-md-0 text-md-end">
              Results: {filteredProperties.length} Listing
              {filteredProperties.length !== 1 ? "s" : ""}
            </p>
          </Col>
        </Row>

        {loading ? (
          <p className="text-center mt-5">Loading properties...</p>
        ) : error ? (
          <p className="text-center mt-5 text-danger">Error: {error}</p>
        ) : filteredProperties.length === 0 ? (
          <p className="text-center mt-5">
            No properties found matching your criteria. Try adjusting your
            filters.
          </p>
        ) : (
          <>
            <Row>
              {currentProperties.map((property, idx) => {
                const imageUrls = images[property.ListingKey];
                const imageSrc = imageUrls && imageUrls[0];

                return (
                  <Col md={4} sm={6} xs={12} key={idx} className="mb-4">
                    <div
                      className="h-100"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/property/${property.ListingKey}`)
                      }
                    >
                      <Card className="h-100 d-flex flex-column property__card p-0 overflow-hidden">
                        {/* Image Section */}
                        <div className="position-relative">
                          {imageSrc ? (
                            <Card.Img
                              variant="top"
                              src={imageSrc}
                              alt="Property"
                              className="w-100"
                              style={{ height: "220px", objectFit: "cover" }}
                            />
                          ) : (
                            <div
                              className="w-100 bg-light d-flex align-items-center justify-content-center"
                              style={{ height: "220px" }}
                            >
                              <span className="text-muted">
                                Loading Images...
                              </span>
                            </div>
                          )}

                          {/* Left - Time */}
                          <div
                            className="fw-semibold position-absolute bottom-0 start-0 m-2 text-white px-2 py-1 rounded"
                            style={{
                              fontSize: "0.75rem",
                              backgroundColor: "#71bf44",
                            }}
                          >
                            {property.TransactionType}
                          </div>

                          {/* Right - Transaction Type */}
                          <div
                            className="fw-semibold position-absolute bottom-0 end-0 m-2 text-white px-2 py-1 rounded"
                            style={{
                              fontSize: "0.75rem",
                              backgroundColor: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {moment(
                              property.SystemModificationTimestamp,
                            ).format("h") + " hours ago"}
                          </div>
                        </div>

                        {/* Content Section */}
                        <Card.Body className="d-flex flex-column justify-content-between px-3 py-2">
                          {/* Price + Heart Icon Row */}
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h5 className="fw-bold mb-0">
                              $ {property.ListPrice.toLocaleString()}
                            </h5>
                            <Button
                              variant="link"
                              className="p-0 m-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(property.ListingKey);
                              }}
                            >
                              {favorites.includes(property.ListingKey) ? (
                                <FaHeart
                                  className="text-danger"
                                  style={{ fontSize: "1.5rem" }}
                                />
                              ) : (
                                <FaRegHeart
                                  style={{ fontSize: "1.5rem", color: "red" }}
                                />
                              )}
                            </Button>
                          </div>

                          {/* Bed/Bath/Sqft */}
                          <p className="text-muted mb-2">
                            {property.BedroomsTotal || "N/A"} <FaBed /> &nbsp;{" "}
                            {property.BathroomsTotalInteger || "N/A"} <FaBath />{" "}
                            &nbsp;
                            {property.LivingArea || "N/A"} <FaRulerCombined />
                          </p>

                          {/* Address */}
                          <Card.Text
                            className="listingCardAddress mb-0"
                            style={{ fontSize: "0.95rem" }}
                          >
                            {property.UnparsedAddress}
                          </Card.Text>

                          {/* MLS Info */}
                          <div
                            className="mt-2"
                            style={{ fontSize: "1 rem", color: "#9e9e9e" }}
                          >
                            {property.StreetName}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </Col>
                );
              })}
            </Row>

            <Row className="mt-3">
              <Col className="text-center text-muted">
                Showing {indexOfFirstProperty + 1} to{" "}
                {Math.min(indexOfLastProperty, filteredProperties.length)} of{" "}
                {filteredProperties.length} properties
              </Col>
            </Row>

            {totalPages > 1 && (
              <Row className="mt-4">
                <Col className="d-flex justify-content-center">
                  <Pagination className="custom-pagination">
                    {renderPaginationItems()}
                  </Pagination>
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default PropertyList;
